import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Play, ArrowLeft, Crown, Check, Sparkles, Shield, Tv, Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import VODFooter from '../components/VODFooter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODMembership = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    if (token) {
      const customerData = localStorage.getItem('livestreamCustomer');
      if (customerData) {
        setCustomer(JSON.parse(customerData));
      }
      fetchSubscriptionStatus(token);
    }
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/api/vod/membership/plans`);
      setPlans(res.data);
    } catch (err) {
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async (token) => {
    try {
      const res = await axios.get(`${API}/api/vod/membership/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.subscription && res.data.subscription.status === 'active') {
        setCurrentSubscription(res.data.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const handleSubscribe = async (tier) => {
    if (!customer) {
      navigate('/live/login?from=vod&redirect=/vod/membership');
      return;
    }

    if (currentSubscription) {
      alert('You already have an active subscription');
      return;
    }

    setSubscribing(tier);
    try {
      const token = localStorage.getItem('livestreamToken');
      const res = await axios.post(
        `${API}/api/vod/payment/subscribe`,
        {
          tier,
          success_url: `${window.location.origin}/vod/subscription-success`,
          cancel_url: window.location.href
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error('Subscribe error:', err);
      alert(err.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/vod')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/vod')}>
            <img 
              src="/ltv-icon.png" 
              alt="LTV Logo" 
              className="w-8 h-8 object-contain brightness-0 invert"
            />
            <span className="font-bold">LTV VOD</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/30 via-black to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-500/20 via-transparent to-transparent" />
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Crown className="h-16 w-16 mx-auto mb-6 text-yellow-400" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Unlock unlimited access to our entire content library. 
            Watch your favourite shows, podcasts, and exclusive content anytime.
          </p>
        </div>
      </div>

      {/* Current Subscription Banner */}
      {currentSubscription && (
        <div className="max-w-4xl mx-auto px-4 -mt-8 mb-8">
          <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/50 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-green-400 flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  You have an active {currentSubscription.tier} subscription
                </h3>
                <p className="text-gray-400 mt-1">
                  Expires: {new Date(currentSubscription.expires_at).toLocaleDateString()}
                </p>
              </div>
              <Button
                onClick={() => navigate('/vod/my-library')}
                className="bg-green-600 hover:bg-green-500"
              >
                Go to My Library
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Plans */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        {/* Premium Plan - Full Width Modern Design */}
        <div className="relative rounded-3xl overflow-hidden">
          {/* Gradient Border */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 opacity-30" />
          <div className="absolute inset-[1px] bg-gradient-to-br from-gray-900 via-purple-950/50 to-gray-900 rounded-3xl" />
          
          <div className="relative p-8 md:p-10">
            <Badge className="mb-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 px-4 py-1">
              <Sparkles className="h-4 w-4 mr-1" />
              PREMIUM MEMBERSHIP
            </Badge>
            
            {/* Price and Benefits in Grid */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left Side - Price */}
              <div>
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  <Crown className="h-6 w-6 text-yellow-400" />
                  {plans?.premium?.name || 'Premium'}
                </h2>
                <p className="text-gray-400 mb-6">Unlock the full LTV VOD experience</p>
                
                <div className="mb-6">
                  {plans?.premium?.is_free ? (
                    <span className="text-5xl font-black text-green-400">FREE</span>
                  ) : (
                    <>
                      <span className="text-6xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                        £2.99
                      </span>
                      <span className="text-xl text-gray-400"> a month</span>
                    </>
                  )}
                </div>
                
                <Button
                  onClick={() => handleSubscribe('premium')}
                  disabled={subscribing === 'premium' || currentSubscription}
                  className="w-full md:w-auto px-10 py-6 text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 rounded-full font-bold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all hover:scale-105"
                >
                  {subscribing === 'premium' ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : currentSubscription ? (
                    currentSubscription.tier === 'premium' ? 'Currently Subscribed' : 'Upgrade to Premium'
                  ) : plans?.premium?.is_free ? (
                    'Get Free Premium'
                  ) : (
                    <>
                      <Crown className="h-5 w-5 mr-2" />
                      Get Premium Now
                    </>
                  )}
                </Button>
              </div>
              
              {/* Right Side - Benefits Grid */}
              <div className="grid grid-cols-2 gap-3">
                {/* No Ads - Highlighted */}
                <div className="col-span-2 bg-gradient-to-r from-green-500/20 to-emerald-500/10 rounded-xl p-4 border border-green-500/30 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-green-400">No Ads</h4>
                    <p className="text-xs text-gray-400">Uninterrupted viewing experience</p>
                  </div>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-amber-400 font-bold text-xs">4K</span>
                    <span className="font-semibold text-sm">HD Quality</span>
                  </div>
                  <p className="text-xs text-gray-500">Crystal clear</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="h-4 w-4 text-purple-400" />
                    <span className="font-semibold text-sm">All Content</span>
                  </div>
                  <p className="text-xs text-gray-500">Exclusive access</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="font-semibold text-sm">Any Device</span>
                  </div>
                  <p className="text-xs text-gray-500">Watch anywhere</p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="h-4 w-4 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="font-semibold text-sm">Early Access</span>
                  </div>
                  <p className="text-xs text-gray-500">New releases first</p>
                </div>
              </div>
            </div>
            
            {/* Cancel Anytime Note */}
            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-gray-400">
                <Check className="inline h-4 w-4 text-green-400 mr-1" />
                Cancel anytime • No commitment • Instant access
              </p>
            </div>
          </div>
        </div>

        {/* Free Content Notice */}
        <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10 text-center">
          <p className="text-gray-400">
            <span className="text-green-400 font-semibold">Free content available!</span>
            {' '}Browse our library without subscribing. Premium unlocks exclusive content.
          </p>
        </div>

        {/* Features */}
        <div className="mt-20">
          <h2 className="text-2xl font-bold text-center mb-12">Why Subscribe?</h2>
          
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                <Tv className="h-7 w-7 text-purple-400" />
              </div>
              <h3 className="font-bold mb-2">Unlimited Access</h3>
              <p className="text-gray-500 text-sm">Watch as much as you want, whenever you want</p>
            </div>
            
            <div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                <Zap className="h-7 w-7 text-purple-400" />
              </div>
              <h3 className="font-bold mb-2">New Content Weekly</h3>
              <p className="text-gray-500 text-sm">Fresh shows, podcasts, and exclusive content</p>
            </div>
            
            <div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                <Shield className="h-7 w-7 text-purple-400" />
              </div>
              <h3 className="font-bold mb-2">Cancel Anytime</h3>
              <p className="text-gray-500 text-sm">No contracts, no commitments</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

export default VODMembership;
